<template>
  <teleport to="body">
    <transition name="guide-fade">
      <div v-if="visible" class="guide-body">
        <!-- 覆盖层 -->
        <div
          ref="overlayLayerRef"
          :style="{ zIndex: computedZIndex }"
          :class="ns.e('overlay')"
          @touchmove.prevent
        />

        <!-- mask蒙层 -->
        <div
          ref="highlightLayerRef"
          :class="highlightMaskCls"
          :style="{
            zIndex: computedZIndex + 1,
            ...highlightStyle,
            ...positionStyle,
          }"
        />

        <!-- step面板 -->
        <jd-tooltip
          ref="popoverRef"
          :key="Date.now()"
          :visible="visible"
          :placement="stepInfo.placement || 'auto'"
          :show-arrow="showCenter ? false : showArrow"
          :offset="stepInfo.offset || 12"
          :popper-class="`
            ${ns.e('panel')}
            ${ns.is(centerCls)}
            ${ns.is(stepInfo?.color || color ? 'colorful' : '')}
            ${panelClass}
          `"
          :popper-style="{
            '--guide-bg-color': stepInfo?.color || color,
          }"
          :z-index="computedZIndex + 2"
          :effect="effect"
          :ignore-form-disabled="ignoreFormDisabled"
        >
          <div
            :class="ns.e('trigger')"
            :style="{
              zIndex: computedZIndex + 2,
              ...highlightStyle,
              ...positionStyle,
            }"
          />
          <template #content>
            <div :class="ns.e('inner')">
              <div
                v-if="stepInfo.extraSlot && $slots[stepInfo.extraSlot]"
                :class="ns.e('extra')"
              >
                <slot name="extra" />
              </div>
              <div
                v-if="closable"
                :class="ns.e('close-icon')"
                @click="handleClose"
              >
                <Close />
              </div>

              <!-- 面板封面 -->
              <div
                v-if="stepInfo.cover || stepInfo.coverSlot"
                :class="`${ns.e('cover')} ${
                  closable ? ns.e('cover-with-close') : ''
                }`"
              >
                <slot
                  v-if="stepInfo.coverSlot && $slots[stepInfo.coverSlot]"
                  :name="stepInfo.coverSlot"
                />
                <component
                  :is="stepInfo.cover?.type"
                  v-else-if="stepInfo.cover"
                  v-bind="stepInfo.cover?.props"
                />
              </div>

              <!-- 面板标题 -->
              <div
                v-if="stepInfo.title || stepInfo.titleSlot"
                :class="ns.e('title')"
              >
                <slot
                  v-if="stepInfo.titleSlot && $slots[stepInfo.titleSlot]"
                  :name="stepInfo.titleSlot"
                />
                <template v-else-if="typeof stepInfo.title === 'string'">
                  {{ stepInfo.title }}
                </template>
              </div>

              <!-- 面板内容 -->
              <div
                v-if="stepInfo.description || stepInfo.descSlot"
                :class="ns.e('description')"
              >
                <slot
                  v-if="stepInfo.descSlot && $slots[stepInfo.descSlot]"
                  :name="stepInfo.descSlot"
                />
                <template v-else-if="typeof stepInfo.description === 'string'">
                  {{ stepInfo.description }}
                </template>
              </div>

              <!-- 面板footer -->
              <div :class="ns.e('footer')">
                <!-- 指示器 -->
                <div v-if="total > 1" class="indicators">
                  <slot
                    v-if="$slots.indicators"
                    name="indicators"
                    :scope="{ current, total }"
                    :current="current"
                    :total="total"
                  />
                  <template v-else>
                    <span class="current">{{ current + 1 }}</span>
                    <span class="total">/{{ total }}</span>
                  </template>
                  <span v-if="skip" class="skip" @click="handleClose">{{
                    t('el.guide.skip')
                  }}</span>
                </div>
                <!-- 按钮 -->
                <div v-if="total === 1" class="buttons" />
                <div class="buttons">
                  <jd-button
                    v-if="current >= 1"
                    plain
                    size="small"
                    class="prev"
                    @click="handlePrev"
                    :ignore-form-disabled="ignoreFormDisabled"
                  >
                    {{ t('el.guide.previous') }}
                  </jd-button>
                  <jd-button
                    v-if="total === 1"
                    type="primary"
                    size="small"
                    class="finish"
                    @click="handleFinish"
                    :ignore-form-disabled="ignoreFormDisabled"
                  >
                    {{ t('el.guide.finishNoStep') }}
                  </jd-button>
                  <jd-button
                    v-else
                    type="primary"
                    size="small"
                    class="next"
                    @click="handleNext"
                    :ignore-form-disabled="ignoreFormDisabled"
                  >
                    {{
                      current >= total - 1
                        ? t('el.guide.finish')
                        : t('el.guide.next')
                    }}
                  </jd-button>
                </div>
              </div>
            </div>
          </template>
        </jd-tooltip>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  watch,
} from 'vue'
import { isEqual } from 'lodash-unified'
import { isNumber } from '@vueuse/core'
import { Close } from '@jd/jdesign-icons-vue'
import { useLocale, useNamespace, useZIndex } from '@jdesign-vue/hooks'
import JdTooltip from '@jdesign-vue/components/tooltip'
// import { useFormDisabled } from '@jdesign-vue/components/form'
import { calcHLStyle, scrollToViewport } from './utils'

import { guideEmits, guideProps } from './guide'
import type { StepType } from './guide'

defineOptions({
  name: 'JdGuide',
})

const props = defineProps(guideProps)

const emits = defineEmits(guideEmits)

// 暂时不关联disabled配置，禁用状态中的表单还是允许展示向导
// const disabled = useFormDisabled()

const { mask, current: propsCurrent } = toRefs(props)

const highlightMaskCls = computed(() =>
  mask.value ? ns.e('highlight-mask') : ns.e('highlight-no-mask')
)
const { t } = useLocale()

const { nextZIndex } = useZIndex()
const defaultZIndex = nextZIndex()
const computedZIndex = computed(() =>
  isNumber(props?.zIndex) ? props?.zIndex : defaultZIndex
)
const ns = useNamespace('guide')

const positionStyle = {
  '--position-type': props.positionType || 'absolute',
}

// 覆盖层，用于覆盖所有元素 防止页面滚动导致高亮区域被滚到其他元素之上
const overlayLayerRef = ref()
const styleEl = ref()
// 高亮层，用于高亮元素
const highlightLayerRef = ref()
const popoverRef = ref()

const highlightStyle = ref()
const showCenter = ref(false)

const current = ref(propsCurrent.value || 0)
const total = ref(props.steps?.length || 0)
const stepInfo = computed<StepType>(() => props.steps?.[current.value])
const centerCls = computed(() => (showCenter.value ? 'center' : 'auto'))

const findTarget = (target) => {
  if (typeof target === 'function') {
    return target()
  } else {
    return document.querySelector(target) as HTMLElement
  }
}
const findDom = (
  target: keyof HTMLElementTagNameMap | (() => HTMLElement) | undefined
) => {
  if (!target) {
    highlightStyle.value = {
      width: `0px`,
      left: `50%`,
      height: `0px`,
      top: `50%`,
    }
    showCenter.value = true
    return
  }
  const realDom = findTarget(target)
  scrollToViewport(realDom)
  nextTick(() => {
    highlightStyle.value = calcHLStyle({
      realDom,
      positionType: props.positionType,
    })
  })
  showCenter.value = false
}

const genFnParams = (): { current: number; total: number } => ({
  current: current.value + 1,
  total: total.value,
})
const handleClose = () => {
  emits('close', genFnParams())
}

const handleFinish = () => {
  emits('finish', genFnParams())
  handleClose()
}

const handlePrev = () => {
  if (current.value <= 0) return
  current.value -= 1
  emits('prev', genFnParams())
}

const handleNext = () => {
  if (current.value >= total.value - 1) {
    handleFinish()
    return
  }
  current.value += 1
  emits('next', genFnParams())
}

const removeLockStyle = () => {
  styleEl.value?.parentNode?.removeChild?.(styleEl.value)
}

const insertLockStyle = () => {
  styleEl.value = document.createElement('style')
  styleEl.value.dataset.id = `jd_lock_overlay`
  styleEl.value.innerHTML = `
    html body {
      overflow-y: hidden;
      transition: margin 300ms cubic-bezier(0.7, 0.3, 0.1, 1) 0s;
      width: 100%;
    }
  `
  document.head.appendChild(styleEl.value)
}

const resize = () => {
  const { target } = stepInfo.value
  findDom(target)
}

let animationFrameId = 0
let cacheDomRect = {}
const cancelAnimation = () => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId)
}
const updateStyleByAnimation = (target) => {
  // 有 id 时,关闭
  cancelAnimation()
  if (!target) {
    cancelAnimation()
    return
  }
  const animate = () => {
    const realDom = findTarget(target)
    const { width, height } = realDom.getBoundingClientRect()
    const compareObj = { width, height }
    const equal = isEqual(compareObj, cacheDomRect)
    if (!equal) {
      cacheDomRect = compareObj
      findDom(target)
    }
    // 递归调用
    animationFrameId = requestAnimationFrame(animate)
  }
  // 启动
  animationFrameId = requestAnimationFrame(animate)
}

watch(
  () => current.value,
  (newCur) => {
    const { target } = props.steps[newCur]
    findDom(target)
    // 如果开启自动侦测, 那么直接启动帧动画
    if (props.autoDetect) updateStyleByAnimation(target)
  }
)

// 每次开启, 立即将页码重置到第0项
watch(
  () => props.visible,
  (newVis) => {
    if (!newVis) {
      cancelAnimation()
      removeLockStyle()
      window.removeEventListener('resize', resize)
      return
    }
    if (props.mask) insertLockStyle()
    window.addEventListener('resize', resize)
    current.value = propsCurrent.value || 0
    resize()
  }
)

// 初始化
onMounted(() => {
  if (props.visible) {
    resize()
  }
})
onUnmounted(() => {
  cancelAnimation()
})
</script>
